/**
 * What a schedule page says about the people on it: how many were on call,
 * and why a colleague's share is smaller (they joined, or their last day
 * came, during the schedule). Pure, so the rules are tested without a
 * browser.
 */
import { compareDates, type ISODate } from '../dates/iso.ts';

/**
 * How many different physicians are on call in these weeks. Not the size of
 * the roster: a past schedule had the people of its day, and someone who
 * joined later, or left before, was not on it.
 */
export function physiciansOnCall(weeks: readonly { physicianId: string | null }[]): number {
  const ids = new Set<string>();
  for (const w of weeks) if (w.physicianId !== null) ids.add(w.physicianId);
  return ids.size;
}

export interface TenureNote {
  kind: 'joined' | 'last-day';
  date: ISODate;
  /** The date lies outside the schedule's own dates, so the year is shown with it. */
  withYear: boolean;
}

/**
 * The notes beside a physician's name in a schedule's distribution: "joined
 * {start}" when their start date is after the schedule's first day, and
 * "last day {end}" when their end date is before its last day. Either one
 * explains a smaller count, or a 0 on a schedule from before they joined or
 * after they left.
 *
 * The dates mean what the scheduling rules mean (availability.ts): a week is
 * theirs only when its Monday is on or after the start date and its Sunday
 * is on or before the end date, so the end date is their last day on the
 * rotation, inclusive. A blank start date means "on the rotation since
 * before our records": nothing to explain, so no note. Only a date strictly
 * inside or beyond the schedule earns a note; a start on the schedule's own
 * first day, or an end on its last, changes nothing.
 */
export function tenureNotes(
  physician: { startDate: ISODate | null; endDate: ISODate | null },
  schedule: { startDate: ISODate; endDate: ISODate },
): TenureNote[] {
  const outside = (date: ISODate) => compareDates(date, schedule.startDate) < 0 || compareDates(date, schedule.endDate) > 0;
  const notes: TenureNote[] = [];
  if (physician.startDate !== null && compareDates(physician.startDate, schedule.startDate) > 0) {
    notes.push({ kind: 'joined', date: physician.startDate, withYear: outside(physician.startDate) });
  }
  if (physician.endDate !== null && compareDates(physician.endDate, schedule.endDate) < 0) {
    notes.push({ kind: 'last-day', date: physician.endDate, withYear: outside(physician.endDate) });
  }
  return notes;
}
